import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  importFromSheet,
  listRequests,
  patchRequest,
  softDeleteRequest,
  submitToHrp,
} from '../services/api';
import { useAutosave } from '../hooks/useAutosave';
import type {
  WorkgroupRequest,
  WorkgroupRequestPatch,
} from '../types/api';

const EDITABLE_FIELDS: Array<{
  key: keyof WorkgroupRequestPatch;
  label: string;
  multiline?: boolean;
}> = [
  { key: 'projectName', label: 'Workgroup Project Name' },
  { key: 'projectDescription', label: 'Workgroup Project Description', multiline: true },
  { key: 'submitterFirstNameLastInitial', label: 'Submitter Name' },
  { key: 'submitterContactEmail', label: 'Submitter Email' },
  { key: 'submitterPhone', label: 'Submitter Phone' },
  { key: 'whyNeeded', label: 'Why is this project needed?', multiline: true },
  { key: 'intendedAccomplishment', label: 'What is this meant to accomplish?', multiline: true },
  { key: 'projectGoal', label: 'Workgroup Project Goal', multiline: true },
  { key: 'estimatedLength', label: 'Estimated Lifetime / Length' },
  { key: 'membersNeeded', label: 'How many members are needed?' },
  { key: 'suggestedQualifications', label: 'Suggested Qualifications', multiline: true },
  { key: 'willingToServeAsMember', label: 'Willing to serve as a member?' },
  { key: 'willingToServeAsLeader', label: 'Willing to serve as leader?' },
  { key: 'fdAdditionalNotes', label: 'FD Additional Notes', multiline: true },
];

export function NewRequestsTab({ onChange }: { onChange?: () => void }) {
  const { user } = useAuth();
  const isFd = user?.role === 'fd';

  const [requests, setRequests] = useState<WorkgroupRequest[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<WorkgroupRequest | null>(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importMessage, setImportMessage] = useState<string | null>(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const rows = await listRequests();
      const filtered = rows.filter(
        (r) =>
          r.status === 'new' ||
          r.status === 'under_review' ||
          r.status === 'deleted',
      );
      setRequests(filtered);
      onChange?.();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = useMemo(
    () => requests.find((r) => r.id === selectedId) ?? null,
    [requests, selectedId],
  );

  useEffect(() => {
    setDraft(selected ? { ...selected } : null);
  }, [selected]);

  const autosave = useAutosave<WorkgroupRequestPatch & { id: string }>({
    save: async ({ id, ...patch }) => {
      const updated = await patchRequest(id, patch);
      setRequests((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    },
    debounceMs: 800,
  });

  const handleFieldChange = (
    key: keyof WorkgroupRequestPatch,
    value: string,
  ): void => {
    if (!draft || !isFd) return;
    const next = { ...draft, [key]: value };
    setDraft(next);
    autosave.trigger({ id: draft.id, [key]: value } as WorkgroupRequestPatch & {
      id: string;
    });
  };

  const handleImport = async () => {
    setImporting(true);
    setImportMessage(null);
    try {
      const result = await importFromSheet();
      setImportMessage(
        `Imported ${result.inserted} new request${result.inserted === 1 ? '' : 's'} ` +
          `(skipped ${result.skipped} already-imported / ${result.totalRowsInSheet} total in sheet).`,
      );
      await refresh();
    } catch (err) {
      setImportMessage(
        err instanceof Error ? `Import failed: ${err.message}` : 'Import failed',
      );
    } finally {
      setImporting(false);
    }
  };

  const handleSoftDelete = async () => {
    if (!selected) return;
    if (!confirm(`Mark "${selected.projectName || 'this request'}" as deleted?`)) return;
    const updated = await softDeleteRequest(selected.id);
    setRequests((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    onChange?.();
  };

  const handleSubmitToHrp = async () => {
    if (!selected) return;
    if (
      !confirm(
        `Submit "${selected.projectName || 'this request'}" to HRP for staffing? ` +
          `It will leave this tab and appear in HRP Staffing.`,
      )
    )
      return;
    const updated = await submitToHrp(selected.id);
    setRequests((prev) => prev.filter((r) => r.id !== updated.id));
    setSelectedId(null);
    onChange?.();
  };

  return (
    <div className="row g-3">
      <div className="col-12 d-flex justify-content-between align-items-center">
        <div>
          <strong>{requests.length}</strong> request{requests.length === 1 ? '' : 's'} in queue
          {loading && <span className="text-secondary ms-2">(refreshing…)</span>}
        </div>
        {isFd && (
          <div>
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={handleImport}
              disabled={importing}
            >
              {importing ? 'Pulling…' : 'Pull new requests from sheet'}
            </button>
          </div>
        )}
      </div>

      {importMessage && (
        <div className="col-12">
          <div className="alert alert-info py-2 mb-0">{importMessage}</div>
        </div>
      )}

      <div className="col-md-4">
        <div className="card">
          <div className="list-group list-group-flush" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {requests.length === 0 && !loading && (
              <div className="list-group-item text-secondary">
                No requests yet. {isFd ? 'Click "Pull new requests from sheet".' : 'FD has not imported any requests yet.'}
              </div>
            )}
            {requests.map((r) => (
              <button
                key={r.id}
                type="button"
                className={`list-group-item list-group-item-action ${
                  selectedId === r.id ? 'active' : ''
                } ${r.status === 'deleted' ? 'text-secondary' : ''}`}
                onClick={() => setSelectedId(r.id)}
              >
                <div className="d-flex justify-content-between">
                  <strong style={r.status === 'deleted' ? { textDecoration: 'line-through' } : {}}>
                    {r.projectName || '(no name)'}
                  </strong>
                  {r.status === 'deleted' && <span className="badge bg-secondary">deleted</span>}
                  {r.status === 'under_review' && <span className="badge bg-blue-lt">in review</span>}
                </div>
                <div className="text-secondary small">
                  {r.submitterFirstNameLastInitial || r.submitterEmail || '—'}
                  {' · '}
                  {new Date(r.submittedAt).toLocaleDateString()}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="col-md-8">
        {!draft ? (
          <div className="card">
            <div className="card-body text-secondary">Select a request to view details.</div>
          </div>
        ) : (
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <div>
                <strong>{draft.projectName || '(no name)'}</strong>
                <span className="text-secondary ms-2">
                  submitted {new Date(draft.submittedAt).toLocaleString()}
                </span>
              </div>
              <div>
                <SaveStatus status={autosave.status} error={autosave.error} isFd={isFd} />
              </div>
            </div>
            <div className="card-body">
              {!isFd && (
                <div className="alert alert-info py-2">
                  You're viewing as <strong>{user?.role || 'no role'}</strong>. Fields are read-only.
                </div>
              )}
              {EDITABLE_FIELDS.map((f) => (
                <div className="mb-3" key={f.key}>
                  <label className="form-label">{f.label}</label>
                  {f.multiline ? (
                    <textarea
                      className="form-control"
                      rows={3}
                      readOnly={!isFd}
                      value={(draft[f.key] ?? '') as string}
                      onChange={(e) => handleFieldChange(f.key, e.target.value)}
                    />
                  ) : (
                    <input
                      type="text"
                      className="form-control"
                      readOnly={!isFd}
                      value={(draft[f.key] ?? '') as string}
                      onChange={(e) => handleFieldChange(f.key, e.target.value)}
                    />
                  )}
                </div>
              ))}

              {isFd && draft.status !== 'deleted' && (
                <div className="d-flex gap-2 mt-3">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSubmitToHrp}
                  >
                    Submit to HR for staffing
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-danger ms-auto"
                    onClick={handleSoftDelete}
                  >
                    Delete request
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SaveStatus({
  status,
  error,
  isFd,
}: {
  status: 'idle' | 'saving' | 'saved' | 'error';
  error: string | null;
  isFd: boolean;
}) {
  if (!isFd) return null;
  if (status === 'saving') return <span className="text-secondary small">Saving…</span>;
  if (status === 'saved') return <span className="text-success small">Saved</span>;
  if (status === 'error')
    return (
      <span className="text-danger small">
        Save failed — {error ?? 'check your network'}
      </span>
    );
  return null;
}
